// Calendar-day numerology rules shared by the service and UI in this app.
// Keep the backend, web and mobile copies in sync; no ephemeris dependency.
export interface NumerologyDatedPeriod {
  number: number;
  sequence?: number;
  startDate: string;
  endDate: string;
  durationDays?: number;
  label?: string;
  parentLabel?: string;
}

// Vats-compatible 365-day table. The ninth period takes the calendar remainder.
export const pratyantarDurations: Record<number, number> = {
  1: 8, 2: 16, 3: 24, 4: 32, 5: 41, 6: 49, 7: 57, 8: 65, 9: 73,
};

export const numerologyAnniversary = (day: number, month: number, year: number): Date => {
  // Observe 29 February on 28 February in common years. Always use the original
  // DOB day/month so the anniversary returns to 29 February in leap years.
  const lastDay = new Date(year, month, 0).getDate();
  return new Date(year, month - 1, Math.min(day, lastDay));
};

export const parseNumerologyTargetDate = (value: string | Date): Date => {
  // A date-only request is a local calendar date, not midnight UTC.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
};

export const addNumerologyDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const numerologyDateString = (date: Date): string => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

export const numerologyDayCount = (start: Date, end: Date): number => (
  // Count calendar days even across daylight-saving changes.
  (Date.UTC(end.getFullYear(), end.getMonth(), end.getDate())
    - Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / 86400000
);

export const isNumerologyDateInPeriod = (
  period: Pick<NumerologyDatedPeriod, 'startDate' | 'endDate'>,
  target: Date = new Date()
): boolean => {
  if (!period?.startDate || !period?.endDate) return false;
  const start = parseNumerologyTargetDate(period.startDate);
  const exclusiveEnd = addNumerologyDays(parseNumerologyTargetDate(period.endDate), 1);
  return target >= start && target < exclusiveEnd;
};

export const getNumerologyAntardashaNumber = (day: number, month: number, year: number): number => {
  const weekdayNumbers = [1, 2, 9, 5, 3, 6, 8];
  const sum = day + month + (year % 100) + weekdayNumbers[numerologyAnniversary(day, month, year).getDay()];
  return ((sum - 1) % 9) + 1;
};

export const buildNumerologyAntardashaPeriods = (parent: NumerologyDatedPeriod | null, dob: Date) => {
  if (!parent) return [];
  const exclusiveEnd = addNumerologyDays(parseNumerologyTargetDate(parent.endDate), 1);
  const periods: Array<NumerologyDatedPeriod & { sequence: number; durationDays: number }> = [];
  let start = parseNumerologyTargetDate(parent.startDate);
  while (start < exclusiveEnd) {
    const nextBirthday = numerologyAnniversary(dob.getDate(), dob.getMonth() + 1, start.getFullYear() + 1);
    const end = nextBirthday < exclusiveEnd ? nextBirthday : exclusiveEnd;
    const sequence = periods.length + 1;
    periods.push({
      number: getNumerologyAntardashaNumber(dob.getDate(), dob.getMonth() + 1, start.getFullYear()),
      sequence, startDate: numerologyDateString(start), endDate: numerologyDateString(addNumerologyDays(end, -1)),
      durationDays: numerologyDayCount(start, end), parentLabel: parent.label, label: `Antardasha ${sequence}`,
    });
    start = end;
  }
  return periods;
};

export const buildNumerologyPratyantarPeriods = (parent: NumerologyDatedPeriod | null) => {
  if (!parent) return [];
  const exclusiveEnd = addNumerologyDays(parseNumerologyTargetDate(parent.endDate), 1);
  const periods: Array<NumerologyDatedPeriod & { sequence: number; durationDays: number }> = [];
  let start = parseNumerologyTargetDate(parent.startDate);
  let number = parent.number;
  for (let sequence = 1; sequence <= 9 && start < exclusiveEnd; sequence += 1) {
    const intendedEnd = addNumerologyDays(start, pratyantarDurations[number]);
    // Preserve the first eight fixed durations. The last period ends exactly at
    // the next birthday, covering the extra day in a 366-day birthday year.
    const end = sequence === 9 || intendedEnd > exclusiveEnd ? exclusiveEnd : intendedEnd;
    periods.push({
      number, sequence, startDate: numerologyDateString(start), endDate: numerologyDateString(addNumerologyDays(end, -1)),
      durationDays: numerologyDayCount(start, end), parentLabel: parent.label, label: `Pratyantara ${sequence}`,
    });
    start = end;
    number = number === 9 ? 1 : number + 1;
  }
  return periods;
};
